import { Skeleton, Box, Flex, Text, useToast } from '@chakra-ui/react';
import React, { useState, useMemo } from 'react';

import { CreditAgreement } from '@finley-technologies/finley-types';

import { PortfolioChartWithTableTemplateForCreditFacility } from '../shared/PortfolioChartTemplate';
import {
  chartsInitialEndDateJs,
  chartsInitialStartDateJs,
  ChartTimePeriod,
  ConcentrationLimitFormatMap,
  ConcentrationLimitMappedData,
  defaultSelectedTimePeriod,
  expandConcentrationLimitAggregateInsights,
  ExpandedConcentrationAggregateInsightsData,
  formatChartDataByReportingDate,
  formatConcentrationDataByReportingDateAndKey,
  formatCsvDownloadDataRows,
  getInitialActiveFields,
  onToggleActiveField,
  PortfolioChartConfig,
} from '../shared/portfolioChartUtils';
import { PortfolioDownloadCsvLink } from '../shared/PortfolioDownloadCsvLink';
import { PortfolioReChart } from '../shared/PortfolioReChart';
import { generateExcessConcentrationChartConfig } from './portfolioBorrowingBaseChartConfigs';
import { PortfolioExcessConcentrationChartDetailsTable } from './PortfolioExcessConcentrationChartDetailsTable';
import { colors } from '~/packages/common-frontend/colors';
import {
  getDefaultDashboardDisplayEndDate,
  getDefaultDashboardDisplayStartDate,
  errorToastConfig,
} from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { useGetDailyConcentrationInsightsQuery } from '~/packages/portfolio/requests/requests.gql.generated';

// custom legend where we only have one legend item to represent all of the individual excess concentration
const renderCustomLegend = () => {
  return (
    <Flex>
      <Flex alignItems="center">
        <Box backgroundColor={colors.brick[400]} h="15px" w="20px" />
        <Text
          as="span"
          color="black"
          fontSize="14px"
          fontWeight={500}
          margin="0 40px 0 16px"
        >
          Individual Excess Concentration
        </Text>
      </Flex>
    </Flex>
  );
};

type PortfolioExcessConcentrationChartProps = {
  creditFacilityId: string;
  chartTitle: string;
  concentrationLimitFormatMap: ConcentrationLimitFormatMap;
  creditAgreementConcentrationLimits: CreditAgreement.DomainEntity.ConcentrationLimitProperty[];
};
export const PortfolioExcessConcentrationChart = ({
  creditFacilityId,
  chartTitle,
  concentrationLimitFormatMap,
  creditAgreementConcentrationLimits,
}: PortfolioExcessConcentrationChartProps) => {
  const toast = useToast();
  const [
    selectedBar,
    setSelectedBar,
  ] = useState<ExpandedConcentrationAggregateInsightsData | null>(null);
  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: chartsInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });

  // need both initial date range chart data (returned by query)
  // as well as chart data for time period
  // because we only want to filter chart data for time period on top of initial date range chart data
  // and not include a previously-filtered time period
  const [chartDataForDateRange, setChartDataForDateRange] = useState<
    ExpandedConcentrationAggregateInsightsData[]
  >([]);
  const [chartDataWithTimePeriod, setChartDataWithTimePeriod] = useState<
    ExpandedConcentrationAggregateInsightsData[]
  >([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<ChartTimePeriod>(
    defaultSelectedTimePeriod,
  );

  const [activeFields, setActiveFields] = useState<
    (keyof ExpandedConcentrationAggregateInsightsData)[]
  >([]);

  const [chartConfig, setChartConfig] = useState<PortfolioChartConfig<string>>(
    {},
  );

  const [
    concentrationLimitMappedData,
    setConcentrationLimitMappedData,
  ] = useState<ConcentrationLimitMappedData>({});

  const { dataRows, headers } = useMemo(
    () =>
      formatCsvDownloadDataRows<ExpandedConcentrationAggregateInsightsData>(
        chartDataWithTimePeriod,
        activeFields,
      ),
    [chartDataWithTimePeriod, activeFields],
  );

  const {
    refetch: refetchConcentration,
    data: concentrationData,
    loading: isConcentrationLoading,
  } = useGetDailyConcentrationInsightsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityParams: {
        creditFacilityId,
        reportingDateStart: getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    },
    onCompleted: (data) => {
      const concentrationLimitMappedByReportingDateAndKey = formatConcentrationDataByReportingDateAndKey(
        data.getDailyConcentrationInsights,
      );
      setConcentrationLimitMappedData(
        concentrationLimitMappedByReportingDateAndKey,
      );

      const expandedConcentrationAggregateData = expandConcentrationLimitAggregateInsights(
        data.getDailyConcentrationInsights,
      );

      // we must make sure to set the core date range chart data with all of the data by day
      // this ensures that we have the base by-day data ready to use
      // when going back to a higher-frequency time period view than the currently selected period
      const formattedChartData = formatChartDataByReportingDate<ExpandedConcentrationAggregateInsightsData>(
        {
          chartData: expandedConcentrationAggregateData,
          selectedTimePeriod: ChartTimePeriod.day,
        },
      );
      setChartDataForDateRange(formattedChartData);

      // then set data for the specific time period
      if (selectedTimePeriod !== ChartTimePeriod.day) {
        const formattedChartDataForPeriod = formatChartDataByReportingDate<ExpandedConcentrationAggregateInsightsData>(
          {
            chartData: expandedConcentrationAggregateData,
            selectedTimePeriod,
          },
        );
        setChartDataWithTimePeriod(formattedChartDataForPeriod);
      } else {
        setChartDataWithTimePeriod(formattedChartData);
      }

      setSelectedBar(formattedChartData[formattedChartData.length - 1]);

      const excessConcentrationChartConfig = generateExcessConcentrationChartConfig(
        formattedChartData,
        creditAgreementConcentrationLimits,
      );
      setChartConfig(excessConcentrationChartConfig);

      const initialActiveFields = getInitialActiveFields({
        chartConfig: excessConcentrationChartConfig,
      }) as (keyof ExpandedConcentrationAggregateInsightsData)[];

      setActiveFields(initialActiveFields);
    },
    notifyOnNetworkStatusChange: true,
  });

  if (isConcentrationLoading || !concentrationData) {
    return <Skeleton height="700px" />;
  }

  const onSelectTimePeriod = (timePeriod: ChartTimePeriod) => {
    setSelectedTimePeriod(timePeriod);

    const formattedChartData = formatChartDataByReportingDate<ExpandedConcentrationAggregateInsightsData>(
      {
        chartData: chartDataForDateRange,
        selectedTimePeriod: timePeriod,
      },
    );

    setChartDataWithTimePeriod(formattedChartData);
  };

  return (
    <PortfolioChartWithTableTemplateForCreditFacility
      creditFacilityId={creditFacilityId}
      chartTitle={chartTitle}
      refetch={refetchConcentration}
      chartDataDateRange={chartDataDateRange}
      setChartDataDateRange={setChartDataDateRange}
      selectedTimePeriod={selectedTimePeriod}
      onSelectTimePeriod={onSelectTimePeriod}
    >
      {/* child 1 */}
      <PortfolioReChart
        chartData={chartDataWithTimePeriod}
        selectedTimePeriod={selectedTimePeriod}
        currentSelectedBarReportingDate={
          selectedBar
            ? selectedBar.reportingDate
            : getDefaultDashboardDisplayEndDate()
        }
        onSelectBar={setSelectedBar}
        chartConfig={chartConfig}
        visibleChartComponents={activeFields}
        renderCustomLegend={renderCustomLegend}
      />

      {/* child 2 */}

      {selectedBar && (
        <Box width="100%" mt="64px">
          <PortfolioExcessConcentrationChartDetailsTable
            selectedBarData={selectedBar}
            chartConfig={chartConfig}
            chartTitle={chartTitle}
            activeFields={activeFields}
            onToggleActiveField={(selectedField) =>
              onToggleActiveField({
                selectedField,
                currentActiveFields: activeFields,
                setActiveFields,
              })
            }
            concentrationLimitMappedData={concentrationLimitMappedData}
            concentrationLimitFormatMap={concentrationLimitFormatMap}
          />
          <Flex mt="24px" justifyContent="flex-end">
            <PortfolioDownloadCsvLink
              dataRows={dataRows}
              headers={headers}
              chartTitle={chartTitle}
              selectedTimePeriod={selectedTimePeriod}
            />
          </Flex>
        </Box>
      )}
    </PortfolioChartWithTableTemplateForCreditFacility>
  );
};
